import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { formatEnrollmentCountdown } from '../../helper/NX5/enrollmentToken';

const TokenQrCode = ({ enrollmentToken }) => {
  const [src, setSrc] = useState('');

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(enrollmentToken, {
      margin: 1,
      width: 220,
      errorCorrectionLevel: 'M',
    })
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setSrc('');
      });

    return () => {
      cancelled = true;
    };
  }, [enrollmentToken]);

  if (!src) {
    return <p className="enrollment-qr-status">Preparing QR…</p>;
  }

  return (
    <img
      className="enrollment-qr-image"
      src={src}
      width={220}
      height={220}
      alt="QR code for the enrollment token"
    />
  );
};

const EnrollmentCountdown = ({ expiresAt }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  return (
    <p className="enrollment-countdown" aria-live="polite">
      {formatEnrollmentCountdown(expiresAt, now)}
    </p>
  );
};

const EnrollmentQrModal = ({
  machineUdi,
  enrollmentToken,
  expiresAt,
  error,
  busy,
  certificateIssued,
  onGenerateAgain,
  onClose,
}) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const showCode = Boolean(enrollmentToken) && !certificateIssued;

  return createPortal(
    <div className="enrollment-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="enrollment-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="enrollment-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="enrollment-modal-header">
          <div>
            <h2 id="enrollment-modal-title" className="enrollment-modal-title">Enrollment code</h2>
            <p className="enrollment-modal-subtitle">{machineUdi}</p>
          </div>
          <button
            type="button"
            className="enrollment-modal-close"
            onClick={onClose}
            aria-label="Close enrollment code"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="enrollment-modal-body">
          {busy && <p className="enrollment-qr-status">Generating enrollment code…</p>}

          {!busy && error && (
            <p className="enrollment-modal-error" role="alert">{error}</p>
          )}

          {showCode && (
            <>
              <p className="enrollment-token-label">Enrollment code</p>
              <p className="enrollment-token" translate="no" spellCheck={false}>{enrollmentToken}</p>
              <p className="enrollment-token-note">
                Shown only in this window. It is not saved.
              </p>
              <p className="enrollment-expiry">
                <span>Expires at</span>
                <time dateTime={expiresAt}>{expiresAt}</time>
              </p>
              <EnrollmentCountdown expiresAt={expiresAt} />
              <TokenQrCode enrollmentToken={enrollmentToken} />
              <p className="enrollment-qr-caption">QR code contains the enrollment token.</p>
            </>
          )}
        </div>

        <div className="enrollment-modal-actions">
          <button type="button" className="enrollment-secondary-btn" onClick={onClose}>
            Close
          </button>
          {!busy && !certificateIssued && (showCode || error) && (
            <button type="button" className="fleet-token-btn" onClick={onGenerateAgain}>
              {showCode ? 'Generate again' : 'Try again'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EnrollmentQrModal;
